import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { PageHeader, TableShell, Field } from "@/components/ui-kit";
import { toast } from "sonner";

const KIND_OPTS = [
  { value: "", label: "All kinds" },
  { value: "event", label: "Event" },
  { value: "access", label: "Access" },
];

const LIMIT = 100;
const EMPTY_FILTERS = { kind: "", action: "", user: "", resource_type: "", date_from: "", date_to: "", q: "" };
const DETAILS_TRUNCATE = 80;

function fmtWhen(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export default function Audit() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);
  const [actions, setActions] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/audit/actions")
      .then((r) => setActions(r.data?.actions || []))
      .catch(() => setActions([]));
  }, []);

  const params = useMemo(() => {
    const p = { limit: LIMIT, offset };
    if (filters.kind) p.kind = filters.kind;
    if (filters.action) p.action = filters.action;
    if (filters.user) p.user_id = filters.user;
    if (filters.resource_type) p.resource_type = filters.resource_type;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.q) p.q = filters.q;
    return p;
  }, [filters, offset]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/audit", { params })
      .then((r) => {
        setRows(r.data?.rows || []);
        setTotal(r.data?.total || 0);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const updateFilter = (key, value) => {
    setOffset(0);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const reset = () => {
    setOffset(0);
    setFilters(EMPTY_FILTERS);
  };

  const toggleExpanded = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const exportCsv = () => {
    const token = localStorage.getItem("nemt_token") || "";
    const qs = new URLSearchParams();
    qs.set("auth", token);
    if (filters.kind) qs.set("kind", filters.kind);
    if (filters.action) qs.set("action", filters.action);
    if (filters.user) qs.set("user_id", filters.user);
    if (filters.resource_type) qs.set("resource_type", filters.resource_type);
    if (filters.date_from) qs.set("date_from", filters.date_from);
    if (filters.date_to) qs.set("date_to", filters.date_to);
    if (filters.q) qs.set("q", filters.q);
    window.open(`${API_BASE}/audit/export.csv?${qs.toString()}`, "_blank");
  };

  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + LIMIT, total);
  const canPrev = offset > 0;
  const canNext = offset + LIMIT < total;

  return (
    <div data-testid="audit-page">
      <PageHeader
        subtitle="Compliance"
        title="Audit Log"
        action={
          <div className="flex items-center gap-2">
            <button onClick={reset} className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400" data-testid="audit-reset">
              Reset
            </button>
            <button onClick={exportCsv} className="btn-primary" data-testid="audit-export">
              Export CSV
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Field label="Kind" testId="audit-filter-kind" value={filters.kind} onChange={(e) => updateFilter("kind", e.target.value)} options={KIND_OPTS} />
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Action</label>
          <input
            list="audit-actions-list"
            data-testid="audit-filter-action"
            value={filters.action}
            onChange={(e) => updateFilter("action", e.target.value)}
            placeholder="e.g. ride.create or ride.*"
            className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
          <datalist id="audit-actions-list">
            {actions.map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
        </div>
        <Field label="User ID" testId="audit-filter-user" value={filters.user} onChange={(e) => updateFilter("user", e.target.value)} placeholder="user id" />
        <Field
          label="Resource type"
          testId="audit-filter-resource-type"
          value={filters.resource_type}
          onChange={(e) => updateFilter("resource_type", e.target.value)}
          placeholder="ride, customer, ..."
        />
        <Field label="From" testId="audit-filter-date-from" type="date" value={filters.date_from} onChange={(e) => updateFilter("date_from", e.target.value)} />
        <Field label="To" testId="audit-filter-date-to" type="date" value={filters.date_to} onChange={(e) => updateFilter("date_to", e.target.value)} />
        <Field label="Search" testId="audit-search" value={filters.q} onChange={(e) => updateFilter("q", e.target.value)} placeholder="email or resource id" />
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-slate-500">
          {loading ? "Loading…" : total === 0 ? "0 of 0" : `${from}-${to} of ${total}`}
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={!canPrev}
            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="audit-prev"
          >
            Prev
          </button>
          <button
            disabled={!canNext}
            onClick={() => setOffset((o) => o + LIMIT)}
            className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="audit-next"
          >
            Next
          </button>
        </div>
      </div>

      <TableShell
        columns={["Time", "User", "Role", "Action", "Resource", "IP", "Details"]}
        empty={!loading && rows.length === 0 ? "No audit entries match these filters." : null}
      >
        {rows.map((a) => {
          const detailsStr = JSON.stringify(a.details || {});
          const isLong = detailsStr.length > DETAILS_TRUNCATE;
          const isOpen = !!expanded[a.id];
          const shown = isOpen || !isLong ? detailsStr : `${detailsStr.slice(0, DETAILS_TRUNCATE)}…`;
          return (
            <tr key={a.id} className="tbl-row border-t border-slate-100" data-testid={`audit-row-${a.id}`}>
              <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{fmtWhen(a.at)}</td>
              <td className="px-4 py-2.5">{a.user_email || a.user_name || <span className="text-slate-400">-</span>}</td>
              <td className="px-4 py-2.5 text-xs uppercase tracking-wide text-slate-500">{a.role || "-"}</td>
              <td className="px-4 py-2.5">
                <span
                  className={`inline-flex items-center text-[11px] font-mono px-2 py-0.5 rounded-full border ${
                    a.kind === "access" ? "bg-slate-50 text-slate-600 border-slate-200" : "bg-sky-50 text-sky-800 border-sky-200"
                  }`}
                >
                  {a.action}
                </span>
              </td>
              <td className="px-4 py-2.5 text-xs">
                <div className="text-slate-700">{a.resource_type || "-"}</div>
                {a.resource_id && <div className="text-slate-400 font-mono text-[11px]">{a.resource_id}</div>}
              </td>
              <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{a.ip || "-"}</td>
              <td className="px-4 py-2.5 max-w-[280px]">
                <span className="font-mono text-xs text-slate-600 break-all">{shown}</span>
                {isLong && (
                  <button onClick={() => toggleExpanded(a.id)} className="ml-1.5 text-[#005E82] text-xs hover:underline">
                    {isOpen ? "less" : "more"}
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </TableShell>
    </div>
  );
}
